import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader } from "@/client/components/page-header";
import { PropagationBadge } from "@/client/components/propagation-badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { type PxeMenu, type PxeMenuResponse, pxeApi } from "@/client/features/pxe/pxe-api";
import { ApiError } from "@/client/lib/api";

// While pending, the request is awaiting BCM's reconciler ack; poll so the page
// flips to Propagated without a manual refresh once it lands (the sidecar
// applies in seconds).
const PENDING_POLL_MS = 10_000;

const formSchema = z.object({
  url: z
    .string()
    .trim()
    .min(1, "Enter a menu URL.")
    .max(2048, "That URL is too long.")
    .url("Enter a valid URL.")
    .refine((v) => /^https?:\/\//i.test(v), "URL must start with http:// or https://."),
});

type FormValues = z.infer<typeof formSchema>;

function UpdateForm({
  initialUrl,
  onSaved,
}: {
  initialUrl: string;
  onSaved: (saved: PxeMenu) => void;
}) {
  const deactivated = useAuthStore((s) => s.deactivated);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { url: initialUrl },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const saved = await pxeApi.update(values.url.trim());
      // A no-op re-save of the current URL comes back already propagated; only a
      // real change re-enters pending, so don't promise an ack that won't come.
      toast.success(
        saved.status === "verified"
          ? "That URL is already the propagated menu. No change applied."
          : "Menu URL saved.",
      );
      onSaved(saved);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't save the menu URL. Please try again.",
      );
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem className="sm:max-w-xl">
              <FormLabel>Menu URL</FormLabel>
              <FormControl>
                <Input
                  placeholder="https://boot.example.com/menu.ipxe"
                  className="font-mono"
                  spellCheck={false}
                  autoComplete="off"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                The iPXE script your nodes chain-load on network boot. Saving applies it to your
                tenant - the status below tracks rollout.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-fit"
          disabled={form.formState.isSubmitting || deactivated}
        >
          {form.formState.isSubmitting ? "Saving…" : "Save menu URL"}
        </Button>
      </form>
    </Form>
  );
}

function StatusRow({ pxe }: { pxe: PxeMenu }) {
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <PropagationBadge status={pxe.status} />
        <span className="text-muted-foreground text-sm">
          {pxe.status === "verified"
            ? `Propagated since ${new Date(pxe.verifiedAt ?? pxe.updatedAt).toLocaleString()}.`
            : pxe.status === "failed"
              ? "Provisioning reported a problem. Re-save to retry, or contact support."
              : "Waiting for provisioning to apply and acknowledge this menu."}
        </span>
      </div>
      <p className="font-mono text-sm break-all">{pxe.menuUrl}</p>
      <p className="text-muted-foreground text-xs">
        {pxe.requestedByName ? `Last set by ${pxe.requestedByName} · ` : ""}
        Updated {new Date(pxe.updatedAt).toLocaleString()}
      </p>
    </div>
  );
}

export function PxePage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.kind === "customer" && user.tenantRole === "tenant_admin";

  const [data, setData] = useState<PxeMenuResponse | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  // `silent` suppresses the error toast: background polls shouldn't spam toasts
  // on a transient failure (the page keeps the last good state and retries).
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    try {
      const res = await pxeApi.get();
      setData(res);
      setError(false);
    } catch (err) {
      setError(true);
      if (!opts?.silent) {
        toast.error(err instanceof ApiError ? err.message : "Failed to load the PXE menu.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll only while a request is pending so a reconciler ack surfaces promptly;
  // a settled (Active/Failed) request stops polling. Polls are silent so a
  // transient blip doesn't fire a toast every interval.
  const status = data?.pxe?.status;
  useEffect(() => {
    if (status !== "pending") {
      return;
    }
    const id = setInterval(() => {
      void load({ silent: true });
    }, PENDING_POLL_MS);
    return () => clearInterval(id);
  }, [status, load]);

  const writeEnabled = data?.writeEnabled ?? false;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Boot menu"
        description="Set the iPXE netboot menu loaded by allocated nodes during network boot. Mantle propagates it to provisioning infrastructure and tracks when it goes live."
      />

      <Card>
        <CardHeader>
          <CardTitle>Current menu</CardTitle>
          <CardDescription>The menu URL applied to your tenant and its status.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-5 w-full" />
            </div>
          ) : data?.pxe ? (
            <StatusRow pxe={data.pxe} />
          ) : error ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load the PXE menu. Refresh to try again.
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">
              No menu URL set yet{isAdmin ? "." : ". Ask a tenant admin to configure one."}
            </p>
          )}
        </CardContent>
      </Card>

      {isAdmin ? (
        <Card>
          <CardHeader>
            <CardTitle>Set menu URL</CardTitle>
            <CardDescription>
              An http(s) link to your iPXE menu script. Changing it returns the menu to pending
              until the new URL is confirmed active.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              // Don't render the form until current data arrives - submitting
              // blind could overwrite an existing menu the admin hasn't seen.
              <div className="flex flex-col gap-3">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-9 w-full sm:max-w-xl" />
                <Skeleton className="h-9 w-32" />
              </div>
            ) : error && !data ? (
              <p className="text-muted-foreground text-sm">
                Couldn't load PXE menu settings. Refresh to try again.
              </p>
            ) : !writeEnabled ? (
              <p className="text-muted-foreground text-sm">
                PXE menu management isn't enabled for this environment yet. Contact Mantle support.
              </p>
            ) : (
              <UpdateForm
                key={data?.pxe?.menuUrl ?? ""}
                initialUrl={data?.pxe?.menuUrl ?? ""}
                onSaved={(saved) => {
                  setData((prev) =>
                    prev
                      ? { ...prev, pxe: saved }
                      : { pxe: saved, netboxConfigured: true, writeEnabled: true },
                  );
                }}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="text-muted-foreground py-4 text-sm">
            Only tenant admins can change the PXE menu URL. Contact an admin on your team to make
            changes.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
